import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart drawer behavior with Route-safe cart updates.
 *
 * Behavior:
 * - First add: wait for Horizon to render the refreshed cart, then open once.
 * - Further adds: keep the already-open drawer open.
 * - Quantity changes/removals: keep the drawer and mounted app widgets intact.
 * - Do NOT manually redispatch cart:view after every cart mutation.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /*
     * Restore path may set [open] before this module loads.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Normal drawer-open behavior.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /*
     * Close drawer when installments CTA opens so dialogs
     * don't overlap.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this
        .querySelector('shopify-payment-terms')
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Wait one browser frame.
   */
  #nextFrame = () => {
    return new Promise((resolve) => {
      requestAnimationFrame(resolve);
    });
  };

  /**
   * Get the current cart-items-component.
   */
  #getCartItemsComponent = () => {
    return this.querySelector('cart-items-component');
  };

  /**
   * Wait for Horizon's server-rendered cart morph to actually
   * land in the DOM before opening the drawer.
   *
   * We only need this when opening a CLOSED drawer after Add to Cart.
   *
   * @param {string} previousPayload
   */
  #waitForCartMorph = async (previousPayload) => {
    const timeout = 1600;
    const startedAt = performance.now();

    while (performance.now() - startedAt < timeout) {
      const cartItems = this.#getCartItemsComponent();

      const currentPayload =
        cartItems?.getAttribute('view-event-payload') || '';

      if (
        cartItems &&
        currentPayload &&
        currentPayload !== previousPayload
      ) {
        /*
         * Give Horizon/custom elements two frames to finish
         * their post-morph work.
         */
        await this.#nextFrame();
        await this.#nextFrame();

        return;
      }

      await this.#nextFrame();
    }

    /*
     * Failsafe. Never prevent the drawer from opening.
     */
    console.warn(
      '[cart-drawer] Timed out waiting for cart morph.'
    );
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const drawerWasOpen = Boolean(
      this.#themeDrawer?.isOpen
    );

    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !drawerWasOpen;

    /*
     * Capture the OLD payload so we can tell when Horizon has
     * rendered the new cart.
     */
    const previousPayload =
      this
        .#getCartItemsComponent()
        ?.getAttribute('view-event-payload') || '';

    /*
     * Quick Add may be running inside another modal.
     */
    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest('dialog:modal')
          : null
      );

    /*
     * IMPORTANT:
     *
     * Do not open immediately.
     * Do not redispatch cart:view manually.
     *
     * Horizon's cart-items component will perform the cart morph.
     * Our Route container is protected in Liquid and stays mounted.
     */
    event.promise
      ?.then(async ({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.#updateStickyState();
            });
          });
        };

        if (detail?.didError) {
          settle();
          return;
        }

        /*
         * Drawer was already open when this cart mutation began.
         *
         * This includes:
         * - product #2 / #3 / etc.
         * - quantity changes
         * - item removals
         *
         * Do absolutely nothing to the drawer lifecycle or cart:view.
         * Horizon updates the cart contents while the existing Route
         * widget remains mounted.
         */
        if (drawerWasOpen) {
          settle();
          return;
        }

        /*
         * Closed drawer + quantity/removal/etc:
         * update silently without opening it.
         */
        if (!shouldAutoOpen) {
          settle();
          return;
        }

        /*
         * Closed drawer + Add to Cart:
         * wait until the refreshed cart HTML is actually present.
         */
        await this.#waitForCartMorph(previousPayload);

        const openAndSettle = () => {
          /*
           * Exactly ONE real drawer open.
           *
           * Normal Horizon drawer opening will trigger the normal
           * cart:view behavior for the initial Route mount.
           */
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        /*
         * If Add to Cart came from Quick Add, allow that modal
         * to close and restore focus first.
         */
        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Sticky cart-summary calculation.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}